// src/hooks/useAuth.jsx — v2 with Dual Role (client ↔ student switch)
import { useState, useEffect, createContext, useContext } from "react";
import {
  signInWithEmailAndPassword, signOut, onAuthStateChanged,
  sendPasswordResetEmail, createUserWithEmailAndPassword,
  GoogleAuthProvider, signInWithPopup,
  EmailAuthProvider, linkWithCredential,
  RecaptchaVerifier, signInWithPhoneNumber,
} from "firebase/auth";
import {
  doc, getDoc, setDoc, serverTimestamp,
  getDocs, query, collection, where,
  addDoc, updateDoc, increment,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../firebase/config";
import { unregisterCurrentFcmToken } from "./useNotifications";

const AuthContext = createContext(null);
const sendVerificationCallable = httpsCallable(functions, "sendVerificationEmail");

const DEFAULT_REFERRAL_SETTINGS = {
  enabled: true,
  autoGenerateCodes: true,
  allowedReferrerRoles: ["student"],
  allowedNewUserRoles: ["student"],
  startsAt: "",
  endsAt: "",
  referrerDiscount: 10,
  welcomeDiscount: 5,
};

const normalizeReferralSettings = (settings = {}) => ({
  ...DEFAULT_REFERRAL_SETTINGS,
  ...settings,
  allowedReferrerRoles: Array.isArray(settings.allowedReferrerRoles) ? settings.allowedReferrerRoles : DEFAULT_REFERRAL_SETTINGS.allowedReferrerRoles,
  allowedNewUserRoles: Array.isArray(settings.allowedNewUserRoles) ? settings.allowedNewUserRoles : DEFAULT_REFERRAL_SETTINGS.allowedNewUserRoles,
  referrerDiscount: Number(settings.referrerDiscount || DEFAULT_REFERRAL_SETTINGS.referrerDiscount),
  welcomeDiscount: Number(settings.welcomeDiscount || DEFAULT_REFERRAL_SETTINGS.welcomeDiscount),
});

async function getReferralSettings() {
  try {
    const snap = await getDoc(doc(db, "siteContent", "main"));
    return normalizeReferralSettings(snap.exists() ? snap.data().referral : {});
  } catch {
    return normalizeReferralSettings();
  }
}

function isReferralWindowActive(settings) {
  if (!settings.enabled) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (settings.startsAt && today < new Date(`${settings.startsAt}T00:00:00`)) return false;
  if (settings.endsAt && today > new Date(`${settings.endsAt}T23:59:59`)) return false;
  return true;
}

function roleMatches(profile, allowedRoles) {
  if (!profile || !allowedRoles?.length) return false;
  const roles = new Set([profile.role, profile.userType]);
  if (profile.isAlsoStudent) roles.add("student");
  if (profile.isAlsoClient) roles.add("client");
  return allowedRoles.some((role) => roles.has(role));
}

// ── Referral bonus coupon create karo ─────────────────────────────────────────
async function giveReferralBonus(referrerId, referrerCode, newUserName, discount) {
  const bonusCode = `REF-${referrerCode}-${Date.now().toString().slice(-4)}`;
  await addDoc(collection(db, "coupons"), {
    code:        bonusCode,
    discount,
    description: `Referral bonus — ${newUserName} ne join kiya!`,
    maxUses:     1,
    usedCount:   0,
    active:      true,
    type:        "referral",
    forUserId:   referrerId,
    createdAt:   serverTimestamp(),
  });
  await updateDoc(doc(db, "users", referrerId), {
    referralCount: increment(1),
    referralEarned: increment(discount),
  });
  return bonusCode;
}

// ── Referee ko welcome discount do ────────────────────────────────────────────
async function giveWelcomeDiscount(userId, userName, discount) {
  const welcomeCode = `WELCOME-${userId.slice(0,6).toUpperCase()}`;
  await addDoc(collection(db, "coupons"), {
    code:        welcomeCode,
    discount,
    description: `Welcome discount for ${userName}`,
    maxUses:     1,
    usedCount:   0,
    active:      true,
    type:        "welcome",
    forUserId:   userId,
    createdAt:   serverTimestamp(),
  });
  return welcomeCode;
}

export function AuthProvider({ children }) {
  const [user, setUser]             = useState(null);
  const [profile, setProfile]       = useState(null);
  const [loading, setLoading]       = useState(true);
  // ✅ NEW: activeRole — current mode user chal raha hai (client ya student)
  // localStorage se restore karo taaki refresh pe bhi same rahe
  const [activeRole, setActiveRole] = useState(() => {
    try { return localStorage.getItem("dgh_active_role") || null; } catch { return null; }
  });

  useEffect(() => {
    return onAuthStateChanged(auth, async (fu) => {
      setUser(fu || null);
      if (fu) {
        const snap = await getDoc(doc(db, "users", fu.uid));
        const profileData = snap.exists() ? snap.data() : null;

        // Sync email verification status
        if (fu.emailVerified && profileData && !profileData.emailVerified) {
          await updateDoc(doc(db, "users", fu.uid), {
            emailVerified: true,
            emailVerifiedAt: serverTimestamp()
          });
          profileData.emailVerified = true;
        }

        setProfile(profileData);

        // ✅ activeRole set karo — agar already set hai to wahi rakho, warna default role use karo
        const savedRole = localStorage.getItem("dgh_active_role");
        if (profileData && savedRole && canUseRole(profileData, savedRole)) {
          setActiveRole(savedRole);
        } else if (profileData) {
          const defaultRole = profileData.role;
          setActiveRole(defaultRole);
          try { localStorage.setItem("dgh_active_role", defaultRole); } catch {}
        }
      } else {
        setProfile(null);
        setActiveRole(null);
        try { localStorage.removeItem("dgh_active_role"); } catch {}
      }
      setLoading(false);
    });
  }, []);

  // ✅ Check karo ki user given role use kar sakta hai ya nahi
  function canUseRole(prof, role) {
    if (!prof) return false;
    if (prof.role === "admin") return true;
    // Pure student — sirf student role use kar sakta hai
    if (prof.role === "student" && !prof.isAlsoClient) return role === "student";
    // Pure client — sirf client role use kar sakta hai
    if (prof.role === "client" && !prof.isAlsoStudent) return role === "client";
    // Dual role user (isAlsoClient + isAlsoStudent dono true)
    if (prof.isAlsoClient && prof.isAlsoStudent) return role === "client" || role === "student";
    if (prof.role === "client" && prof.isAlsoStudent) return role === "client" || role === "student";
    if (prof.role === "student" && prof.isAlsoClient) return role === "client" || role === "student";
    return role === prof.role;
  }

  // ✅ NEW: Role switch function
  const switchRole = (newRole) => {
    if (!profile || !canUseRole(profile, newRole)) return false;
    setActiveRole(newRole);
    try { localStorage.setItem("dgh_active_role", newRole); } catch {}
    return true;
  };

  // ✅ Check karo ki user dono roles rakhta hai
  const hasDualRole = (prof) => {
    if (!prof) return false;
    return (prof.role === "client" && prof.isAlsoStudent) ||
           (prof.role === "student" && prof.isAlsoClient) ||
           (prof.isAlsoClient && prof.isAlsoStudent);
  };

  const login         = (email, pass) => signInWithEmailAndPassword(auth, email, pass);
  const logout        = async () => {
    try { localStorage.removeItem("dgh_active_role"); } catch {}
    await unregisterCurrentFcmToken(auth.currentUser?.uid).catch(() => {});
    return signOut(auth);
  };
  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  const sendVerification = async (email) => {
    try {
      const res = await sendVerificationCallable({ email });
      if (res.data?.success) {
        if (user?.uid) {
          await updateDoc(doc(db, "users", user.uid), {
            verificationEmailSentAt: serverTimestamp()
          });
        }
        return { success: true };
      }
      return { success: false, error: res.data?.error };
    } catch (err) {
      console.error("Verification error:", err);
      return { success: false, error: err.message };
    }
  };

  const setPasswordForGoogleUser = async (password) => {
    if (!auth.currentUser?.email) throw new Error("No signed-in Google account found.");
    const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
    return linkWithCredential(auth.currentUser, credential);
  };

  // ── Phone Authentication ────────────────────────────────────────────────────
  const setupRecaptcha = (containerId) => {
    if (!auth || !containerId) return null;
    try {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
      }
      window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
        size: "invisible",
        callback: () => {},
      });
      return window.recaptchaVerifier;
    } catch (e) {
      console.error("Recaptcha error:", e);
      return null;
    }
  };

  const loginWithPhone = async (phoneNumber, verifier) => {
    try {
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, verifier);
      return confirmationResult;
    } catch (e) {
      throw e;
    }
  };

  // ── Google Sign In ──────────────────────────────────────────────────────────
  const loginWithGoogle = async (role = "student") => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    const cred = await signInWithPopup(auth, provider);
    const fu   = cred.user;
    const referralSettings = await getReferralSettings();

    const snap = await getDoc(doc(db, "users", fu.uid));
    if (!snap.exists()) {
      const canHaveReferralCode = referralSettings.autoGenerateCodes && roleMatches(
        { role, userType: role === "pending" ? "client" : role, isAlsoClient: role === "pending" || role === "client", isAlsoStudent: role === "student" },
        referralSettings.allowedReferrerRoles
      );
      const refCode = canHaveReferralCode ? fu.uid.slice(0, 8).toUpperCase() : "";
      const newProfile = {
        uid:          fu.uid,
        role,
        userType:      role === "pending" ? "client" : role,
        email:        fu.email || "",
        ownerName:    fu.displayName || "",
        coachingName: fu.displayName || "",
        phone:        fu.phoneNumber || "",
        city:         "",
        photoUrl:     fu.photoURL || "",
        logoUrl:      "",
        totalBilled:  0,
        totalPaid:    0,
        approved:     role === "student" || role === "client",
        emailVerified: false,
        createdAt:    serverTimestamp(),
        googleSignIn: true,
        referralCode: refCode,
        referralCount: 0,
        referralEarned: 0,
        isAlsoClient:  role === "client" || role === "pending",
        isAlsoStudent: role === "student",
      };
      await setDoc(doc(db, "users", fu.uid), newProfile);
      setProfile(newProfile);
      setActiveRole(role);
      try { localStorage.setItem("dgh_active_role", role); } catch {}
    } else {
      const prof = snap.data();
      setProfile(prof);
      const savedRole = localStorage.getItem("dgh_active_role");
      if (role && canUseRole(prof, role)) {
        setActiveRole(role);
        try { localStorage.setItem("dgh_active_role", role); } catch {}
      } else if (savedRole && canUseRole(prof, savedRole)) {
        setActiveRole(savedRole);
      } else {
        setActiveRole(prof.role);
        try { localStorage.setItem("dgh_active_role", prof.role); } catch {}
      }
    }
    return cred;
  };

  // ── Email/Password Sign Up ──────────────────────────────────────────────────
  const signUp = async ({ email, password, name, phone, coachingName, city = "", role = "pending", userType = "client", referralCode = "" }) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const referralSettings = await getReferralSettings();

    // ✅ role aur userType se isAlsoClient/isAlsoStudent set karo
    const isStudent = role === "student" || userType === "student";
    const isClient  = role === "pending" || role === "client" || userType === "client";
    const canHaveReferralCode = referralSettings.autoGenerateCodes && roleMatches(
      { role, userType, isAlsoClient: isClient, isAlsoStudent: isStudent },
      referralSettings.allowedReferrerRoles
    );
    const refCode = canHaveReferralCode ? cred.user.uid.slice(0, 8).toUpperCase() : "";

    const userData = {
      uid:           cred.user.uid,
      role,
      userType,
      email,
      ownerName:     name,
      coachingName:  coachingName || name,
      phone,
      city,
      logoUrl:       "",
      photoUrl:      "",
      totalBilled:   0,
      totalPaid:     0,
      approved:      role === "student" || role === "client",
      emailVerified: false,
      createdAt:     serverTimestamp(),
      referralCode:  refCode,
      referralCount: 0,
      referralEarned: 0,
      isAlsoClient:  isClient,
      isAlsoStudent: isStudent,
    };

    // ── Referral code handle karo ──────────────────────────────────────────
    if (referralCode.trim()) {
      const code = referralCode.trim().toUpperCase();
      userData.referredBy = code;
      try {
        if (
          isReferralWindowActive(referralSettings) &&
          roleMatches({ role, userType, isAlsoClient: isClient, isAlsoStudent: isStudent }, referralSettings.allowedNewUserRoles)
        ) {
          const referrerSnap = await getDocs(query(
            collection(db, "users"),
            where("referralCode", "==", code)
          ));
          if (!referrerSnap.empty) {
            const referrerId   = referrerSnap.docs[0].id;
            const referrerData = referrerSnap.docs[0].data();
            const referrerCode = referrerData.referralCode;
            if (referrerId !== cred.user.uid && roleMatches(referrerData, referralSettings.allowedReferrerRoles)) {
              userData.referrerId = referrerId;
              await giveReferralBonus(referrerId, referrerCode, coachingName || name, referralSettings.referrerDiscount);
              const welcomeCode = await giveWelcomeDiscount(cred.user.uid, coachingName || name, referralSettings.welcomeDiscount);
              userData.welcomeCoupon = welcomeCode;
            } else {
              userData.referralIgnoredReason = "referrer_not_eligible";
            }
          }
        } else {
          userData.referralIgnoredReason = "program_inactive_or_role_not_allowed";
        }
      } catch(e) {
        console.warn("Referral error:", e);
        userData.referralBonusFailed = true; // admin can retry manually
      }
    }

    await setDoc(doc(db, "users", cred.user.uid), userData);
    return cred;
  };

  return (
    <AuthContext.Provider value={{
      user, profile, loading,
      activeRole, switchRole, hasDualRole, canUseRole,
      login, logout, resetPassword, signUp, loginWithGoogle, setPasswordForGoogleUser,
      setupRecaptcha, loginWithPhone, sendVerification,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export default function useAuth() { return useContext(AuthContext); }
